import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';

// Utils
import { closePopup } from '../components/popupPanel/popupActions';
import { getElementId } from '../../common/elements/utils/elementPropertyUtils';
import { isCommentThread, isContent } from '../../common/elements/utils/elementTypeUtils';
import quickNotesCanDrop from '../workspace/header/workspaceToolsHeader/quickNotes/popup/quickNotesCanDrop';
import elementShortcutsDecorator from '../workspace/shortcuts/clipboard/elementShortcutsDecorator';
import quickNotesPopupStayActivePredicate from '../workspace/header/workspaceToolsHeader/quickNotes/popup/quickNotesPopupStayActivePredicate';
import { inboxDropResultFn } from '../element/dnd/dropResultFn';

// Selectors
import { quickNotesElementSelector } from '../workspace/header/workspaceToolsHeader/quickNotes/quickNotesElementsSelector';
import { quickNotesUtilSelector } from '../workspace/header/workspaceToolsHeader/quickNotes/quickNotesSelector';

// Actions
import * as boardService from '../element/board/boardService';

// Decorators
import ElementDropTarget from '../element/dnd/elementDropTargets/ElementDropTarget';
import renderListPlaceholdersDecorator from '../element/elementPlaceholder/renderListPlaceholdersDecorator';

// Components
import QuickNotesPopup from '../workspace/header/workspaceToolsHeader/quickNotes/popup/QuickNotesPopup';
import PopupTrigger from '../components/popupPanel/PopupTrigger';

// Constants
import { DROP_TARGET_TYPES, ELEMENT_DND_TYPE } from '../../common/elements/elementConstants';
import { PopupIds, PopupSections } from '../components/popupPanel/popupConstants';
import { NATIVE_TYPES } from '../utils/dnd/dragAndDropUtils';

const mapDispatchToProps = (dispatch) => ({
    closePopupPanel: () => dispatch(closePopup(PopupIds.QUICK_NOTES)),
    fetchBoard: (boardId) =>
        dispatch(
            boardService.fetchBoard({
                boardId,
                excludeSelf: false,
                loadAncestors: false,
            }),
        ),
});

const getQuickNotesDropLocation = (props, monitor, domNode) => {
    const dropResult = inboxDropResultFn(props, monitor, domNode);

    const itemType = monitor.getItemType();

    if (itemType !== ELEMENT_DND_TYPE) return { ...dropResult };

    // Can't drop lines onto the inbox
    const { draggedElements } = monitor.getItem();
    const acceptedElementIds = draggedElements.filter((el) => isContent(el) || isCommentThread(el)).map(getElementId);

    return {
        ...dropResult,
        acceptedElementIds,
    };
};

const dropTargetConfig = {
    drop: getQuickNotesDropLocation,
    canDrop: quickNotesCanDrop,
    acceptDropTypes: NATIVE_TYPES,
    hoverType: DROP_TARGET_TYPES.INBOX_CLOSED,
};

@connect(quickNotesElementSelector)
@connect(quickNotesUtilSelector, mapDispatchToProps)
@elementShortcutsDecorator
@renderListPlaceholdersDecorator
@ElementDropTarget(dropTargetConfig)
class SidebarQuickNotesUtil extends React.Component {
    componentDidMount() {
        const { quickNotesRootId, fetchBoard } = this.props;
        if (!quickNotesRootId) return;

        fetchBoard(quickNotesRootId);
    }

    componentDidUpdate(prevProps) {
        const { closePopupPanel, quickNotesChildIds } = this.props;

        if (prevProps.quickNotesChildIds.length > 0 && quickNotesChildIds.length === 0) {
            closePopupPanel();
        }
    }

    render() {
        const { onPopupToggle, quickNotesRootId, connectDropTarget, canDrop, isHovered, quickNotesChildIds } =
            this.props;

        if (!quickNotesRootId) return null;

        return (
            <div className={classNames('QuickNotesUtil', { 'can-drop': canDrop, hovered: isHovered })}>
                <div ref={connectDropTarget} className="drop-target-wrapper">
                    <PopupTrigger
                        popupId={PopupIds.QUICK_NOTES}
                        className="button-quick-notes header-util-button"
                        stayActivePredicate={quickNotesPopupStayActivePredicate}
                        closeSection={PopupSections.HEADER}
                        onToggle={onPopupToggle}
                    >
                        {(popupOpen) => (
                            <div className="button-content">
                                <div className="SidebarItemTitle">From your phone</div>
                                <div className="SidebarItemCount">
                                    {' '}
                                    {quickNotesChildIds.length > 0 ? quickNotesChildIds.length : ''}
                                </div>
                            </div>
                        )}
                    </PopupTrigger>
                </div>
                <QuickNotesPopup {...this.props} />
            </div>
        );
    }
}

SidebarQuickNotesUtil.propTypes = {
    tooltipEnabled: PropTypes.bool,
    popupOpen: PropTypes.bool,
    canDrop: PropTypes.bool,
    isHovered: PropTypes.bool,
    quickNotesRootId: PropTypes.string,
    quickNotesChildIds: PropTypes.array,
    fetchBoard: PropTypes.func,
    connectDropTarget: PropTypes.func,
    newQuickNotesCount: PropTypes.number,
    onPopupToggle: PropTypes.func,
    closePopupPanel: PropTypes.func,
};

export default SidebarQuickNotesUtil;
